var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _inflated, _DeferredTemplate_instances, inflate_fn;
import { n as hasInteracted, p as onFirstInteraction, q as activateInteractionAssets, t as lazyReveal } from "./utilities-Da0sjanJ.js";
class DeferredTemplate extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _DeferredTemplate_instances);
    __privateAdd(this, _inflated, false);
  }
  connectedCallback() {
    const tpl = this.querySelector("template");
    if (!tpl) return;
    const triggerAttr = this.dataset.trigger || "pointerenter";
    const triggers = triggerAttr.split(" ").map((t) => t.trim()).filter(Boolean);
    const targetSelector = this.dataset.target;
    const target = targetSelector ? this.closest(targetSelector) || this.parentElement : this.parentElement;
    if (!target) {
      __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
      return;
    }
    const basePolicy = this.dataset.inflateOnInteraction;
    const mobilePolicy = this.dataset.inflateOnInteractionMobile;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;
    const inflatePolicy = isMobile && mobilePolicy ? mobilePolicy : basePolicy;
    if (inflatePolicy === "true" || inflatePolicy === "mobile" && isMobile) {
      if (hasInteracted()) {
        __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
      } else {
        onFirstInteraction(() => __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl), 0);
      }
    } else if (inflatePolicy === "idle" || inflatePolicy === "idle-mobile" && isMobile) {
      if (hasInteracted()) {
        __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
      } else {
        const doInflate = () => __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
        onFirstInteraction(doInflate, 0);
        if ("requestIdleCallback" in window) {
          requestIdleCallback(doInflate, { timeout: 3e3 });
        } else {
          setTimeout(doInflate, 3e3);
        }
      }
    } else if (inflatePolicy === "interaction-idle" || inflatePolicy === "interaction-idle-mobile" && isMobile) {
      const doInflate = () => __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
      const scheduleIdle = () => {
        if ("requestIdleCallback" in window) requestIdleCallback(doInflate, { timeout: 3e3 });
        else setTimeout(doInflate, 500);
      };
      if (hasInteracted()) {
        scheduleIdle();
      } else {
        onFirstInteraction(scheduleIdle, 0);
      }
    }
    if ((triggers.includes("pointerenter") || triggers.includes("mouseenter")) && !triggers.includes("focusin")) {
      triggers.push("focusin");
    }
    const isHovered = (triggers.includes("pointerenter") || triggers.includes("mouseenter")) && target.matches(":hover");
    const isFocused = triggers.includes("focusin") && target.matches(":focus-within");
    if (isHovered || isFocused) {
      __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
      return;
    }
    const inflateHandler = () => __privateMethod(this, _DeferredTemplate_instances, inflate_fn).call(this, tpl);
    triggers.forEach((t) => target.addEventListener(t, inflateHandler, { once: true }));
  }
}
_inflated = new WeakMap();
_DeferredTemplate_instances = new WeakSet();
inflate_fn = function(tpl) {
  if (__privateGet(this, _inflated)) return;
  __privateSet(this, _inflated, true);
  const hadFocus = this.matches(":focus") || this.matches(":focus-within");
  const fragment = document.createDocumentFragment();
  fragment.append(...tpl.content.cloneNode(true).childNodes);
  if (hasInteracted()) {
    activateInteractionAssets(fragment);
  }
  const scripts = fragment.querySelectorAll("script");
  for (const old of scripts) {
    const fresh = document.createElement("script");
    for (const attr of old.attributes) {
      fresh.setAttribute(attr.name, attr.value);
    }
    fresh.textContent = old.textContent;
    old.replaceWith(fresh);
  }
  const childrenToAnimate = Array.from(fragment.children);
  childrenToAnimate.forEach((child) => child.classList.add("is-inflating"));
  let firstFocusable = null;
  if (hadFocus) {
    firstFocusable = fragment.querySelector('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
  }
  this.replaceWith(fragment);
  if (firstFocusable) {
    firstFocusable.focus();
  }
  childrenToAnimate.forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      lazyReveal(child);
      if (child.tagName === "IMG" && child.getAttribute("loading") === "lazy" && !child.hasAttribute("loaded")) {
        lazyReveal(child.parentElement);
      }
    }
  });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      childrenToAnimate.forEach((child) => child.classList.remove("is-inflating"));
    });
  });
};
if (!customElements.get("deferred-template")) {
  customElements.define("deferred-template", DeferredTemplate);
}
